<template>
  <div class="reservations-list">
    <PageTitleExtra
      :title="$t('reservationList.title')"
      :btn="$t('common.button.create.text')"
      :btn2="$t('shiftCalendar.button.closePeriod.text')"
      :btn3="$t('common.button.export.text')"
      btnFeature="admin.appointmentOrder.create"
      btn3Feature="admin.appointmentOrder.createSheetExportTask"
      btn2Feature="admin.appointmentScheduleBan.updateTimes"
      cyBtn1="new-reservation-btn"
      @btnClick="$router.push({ name: 'NewReservationCreate' })"
      @btn2Click="togglePeriodDialog = true"
      @btn3Click="showExportOptions = true"
    />
    <!-- <header class="view-title">預約列表</header> -->
    <div class="filters-wrapper">
      <div class="filter-row">
        <div class="flex w-full gap-[8px]">
          <div class="flex gap-[8px] w-full">
            <el-date-picker
              v-model="search.date"
              editable
              class="test2"
              :class="{'date-picker-width': !showCustomFlow}"
              type="daterange"
              :start-placeholder="$t('reservationList.date.start.placeholder')"
              :end-placeholder="$t('reservationList.date.end.placeholder')"
              :default-time="['00:00:00', '23:59:59']"
              :placeholder="$t('reservationList.date.placeholder')"
              :range-separator="$t('common.input.datepicker.rangeSeparator')"
              @change="refresh(true)"
            />
            <CustomFlowSelect
              :showCustomFlow="showCustomFlow"
              :search="search"
              :displayCustomFlowConfig="displayCustomFlowConfig"
              :customFlowList="customFlowList"
              @refresh="refresh(true)"
            />
          </div>
        </div>
        <div class="flex w-full flex-col gap-[8px]">
          <div class="flex gap-[8px]">
            <BaseElInput
              v-model="search.search"
              :placeholder="$t('reservationList.search.userNameOrPhone.placeholder')"
              testName="search_search"
              class="test2"
              clearable
              @keypress.enter.native="refresh(true)"
              @clear="refresh(true)"
            >
              <i slot="prefix" class="el-input__icon el-icon-search" @click="refresh(true)" />
            </BaseElInput>

            <ServiceUnitSearch
              class="test2"
              testName="search_serviceUnit"
              :model.sync="search.unit"
              @change="refresh"
            />
          </div>
          <div class="flex gap-[8px]">
            <ResourceSearch
              v-if="checkAction('admin.resourceItem.page')"
              class="test2"
              testName="search_resource"
              :model.sync="search.resource"
              @change="refresh"
            />
            <BaseElSelect
              v-model="search.status"
              testName="search_status"
              class="test2"
              :placeholder="$t('reservationCalendar.search.status.placeholder')"
              clearable
              @change="refresh"
            >
              <BaseElSelectOption
                v-for="(item, index) in ['cancel', 'complete', 'wait', 'absence', 'confirm']"
                :key="`status-${index}`"
                :label="statusContent(item)"
                :value="item"
              />
            </BaseElSelect>
          </div>
          <div class="flex gap-[8px]">
            <BaseSearch
              v-if="checkAction('admin.resourceItemCategory.page')"
              class="test2"
              testName="search_resourceCategory"
              :value.sync="search.resourceCategory"
              :api="GetResourceCategory"
              :placeholder="$t('reservationList.search.resourceCategory.placeholder')"
              multiple
              @change="refresh"
            />
          </div>
          <div class="flex gap-[8px]">
            <BaseElInput
              v-model="search.code"
              testName="search_orderCode"
              class="test2"
              clearable
              :placeholder="$t('reservationList.search.orderCode.placeholder')"
              @keypress.enter.native="refresh(true)"
              @clear="refresh(true)"
            >
              <i slot="prefix" class="el-input__icon el-icon-search" @click="refresh" />
            </BaseElInput>
            <BaseElSelect
              v-model="search.replyStatus"
              testName="search_replyStatus"
              class="test2"
              :placeholder="$t('reservationList.search.replyStatus.placeholder')"
              clearable
              @change="refresh"
            >
              <BaseElSelectOption
                v-for="(item, index) in ['pending', 'replied', 'expired', 'no_reply_needed']"
                :key="`replyStatus-${index}`"
                :label="replyStatusContent(item)"
                :value="item"
              />
            </BaseElSelect>
          </div>
          <div v-if="enableCancelReasonCollection" class="flex gap-[8px]">
            <BaseElSelect
              v-model="search.cancelReasonCode"
              testName="search_cancelReasonCode"
              class="test2"
              placeholder="取消原因"
              clearable
              :options="Object.values(cancelReasonConfig)"
              @change="refresh"
            />
          </div>
        </div>
      </div>
    </div>

    <section class="flex justify-between items-end">
      <div class="flex-1">
        <p>{{ $t('reservationList.limit.title', { monthRecordCount: monthRecordCount, contractReservationsCount: contractReservationsCount}) }}</p>
        <ProgressBar :percentage="reservationsPercentage" />
      </div>
      <TableFilter
        style="margin-left: 15px"
        :tableName="tableName"
        :options="reservationListFilterOptions"
        @update="updateTableFilter"
      />
    </section>

    <!-- 快速收款連結區域 -->
    <section v-if="showControl.quickPaymentSection" class="flex items-center mb-4">
      <div v-if="displayData.hasQuickPaymentUrl" class="flex items-center">
        <span class="text-md mr-1 mb-1">{{ displayText.quickPayment.linkText }}{{ displayData.quickPaymentUrl }}</span>
        <el-tooltip :content="displayText.quickPayment.copyTooltip" placement="right">
          <span class="material-icons cursor-pointer text-xl" @click="onCopyQuickPaymentUrl">content_copy_filled</span>
        </el-tooltip>
      </div>
      <div v-else class="flex items-center text-gray-500">
        <span class="text-md mr-1">{{ displayText.quickPayment.notSetText }}</span>
        <el-tooltip
          :content="displayText.quickPayment.notSetTooltip"
          placement="right"
          :popper-class="'max-w-xs'"
        >
          <span class="material-icons text-xl mt-1">help_outline</span>
        </el-tooltip>
      </div>
    </section>

    <section>
      <BaseTable v-if="!tableRefresh" v-loading="loading" :data="reservationsList" :empty-text="$t('common.table.empty.text')">
        <EmptyBlock slot="empty" />
        <BaseElTableColumn prop="code" :label="$t('reservationList.table.orderCode.title')" width="120" fixed align="center" />
        <BaseElTableColumn v-if="showColumn('orderType', tableFilter)" :label="$t('reservationList.table.orderType.title')" fixed align="center">
          <template slot-scope="scope">
            {{ getData(scope.row, 'AppointmentOrder.peopleCount', 1) > 1 ? $t('reservationList.table.orderType.multiple.title') : $t('reservationList.table.orderType.single.title') }}
          </template>
        </BaseElTableColumn>
        <!-- 預約風險等級欄位 -->
        <BaseElTableColumn
          v-if="showControl.riskLevelColumn"
          prop="AppointmentOrder.riskLevel"
          width="120"
          fixed
          align="center"
        >
          <template slot="header">
            <div class="flex items-center gap-1 justify-center">
              <span>{{ displayText.riskLevel.title }}</span>
              <el-tooltip
                :content="displayText.riskLevel.headerTooltip"
                placement="right"
                :popper-class="'max-w-xs'"
              >
                <span class="material-icons text-xl">help_outline</span>
              </el-tooltip>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="flex items-center justify-center">
              <el-tag
                v-if="getRiskLevelDisplay(get(scope.row, 'AppointmentOrder.riskLevel')).value"
                :type="getRiskLevelDisplay(get(scope.row, 'AppointmentOrder.riskLevel')).tag"
                size="small"
              >
                {{ getRiskLevelDisplay(get(scope.row, 'AppointmentOrder.riskLevel')).label }}
              </el-tag>
              <span v-else>-</span>
            </div>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn prop="userName" :label="$t('reservationList.table.userName.title')" fixed align="center">
          <template slot-scope="scope">
            {{ scope.row.userName || (scope.row.numberCode >= 2 ? $t('common.unfilled.text') : '') }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('phone', tableFilter)" prop="userPhone" :label="$t('reservationList.table.userPhone.title')" width="120" align="center">
          <template slot-scope="scope">
            {{ scope.row.userPhone || (scope.row.numberCode >= 2 ? $t('common.unfilled.text') : '') }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('date', tableFilter)" prop="start" :label="$t('reservationList.table.start.date.title')" width="115" align="center">
          <template slot-scope="scope">
            {{ dateTime(scope.row.start).date }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('time', tableFilter)" prop="start" :label="$t('reservationList.table.start.time.title')" width="115" align="center">
          <template slot-scope="scope">
            {{ dateTime(scope.row.start).time }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('checkInTime', tableFilter) && (checkAction('admin.checkInPageSetting.page') || checkAction('planModule.selfCheckIn.enable'))" prop="checkInTime" :label="$t('reservationList.table.checkInTime.title')" width="115" align="center">
          <template slot-scope="scope">
            {{ get(scope.row,'AppointmentOrder.AppointmentOrderCheckIn') ? dateTime(get(scope.row,'AppointmentOrder.AppointmentOrderCheckIn.checkInAt')).dateTime : '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('checkInBranch', tableFilter) && checkAction('admin.checkInPageSetting.page')" prop="checkInBranch" :label="$t('reservationList.table.checkInBranch.title')" width="115" align="center">
          <template slot-scope="scope">
            {{ get(scope.row,'AppointmentOrder.AppointmentOrderCheckIn') ? get(scope.row,'AppointmentOrder.AppointmentOrderCheckIn.Branch.name') : '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('service', tableFilter)" prop="AppointmentService.name" :label="$t('reservationList.table.service.title')" width="150" align="center" />
        <BaseElTableColumn v-if="showColumn('recommendedServices', tableFilter) && checkAction('admin.appointmentServicePairing.page')" :label="$t('reservationList.table.recommendedServices.title')" width="180" align="center">
          <template slot-scope="scope">
            {{ recommendedServicesText(get(scope.row, 'AppointmentService.id')) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('serviceUnit', tableFilter)" prop="AppointmentUnit.name" :label="$t('reservationList.table.serviceUnit.title')" width="150" align="center">
          <template slot-scope="scope">
            {{ showNotSpecify(scope.row) || '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('resourceItem', tableFilter) && checkAction('admin.resourceItem.page')" prop="ResourceItem.name" :label="$t('reservationList.table.resource.title')" width="150" align="center">
          <template slot-scope="scope">
            {{ scope.row.ResourceItem?.name || '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('userAddress', tableFilter) && useMemberAddress" prop="AppointmentOrder.userAddress" :label="$t('reservationList.table.userAddress.title')" align="center" width="180">
          <template slot-scope="scope">
            {{ scope.row.AppointmentOrder.userAddress || '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('vehiclePlate', tableFilter) && checkAction('planModule.keeper.enabled')" prop="AppointmentOrder.customInfo.keeperVehicle.plate" :label="$t('reservationList.table.vehiclePlate.title')" align="center" width="120">
          <template slot-scope="scope">
            {{ get(scope.row, 'AppointmentOrder.customInfo.keeperVehicle.plate') || '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('vehicleSize', tableFilter) && checkAction('planModule.keeper.enabled')" prop="AppointmentOrder.customInfo.keeperVehicle.size" :label="$t('reservationList.table.vehicleSize.title')" align="center" width="120">
          <template slot-scope="scope">
            {{ get(scope.row, 'AppointmentOrder.customInfo.keeperVehicle.size') || '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('origin', tableFilter)" prop="origin" :label="$t('reservationList.table.origin.title')" width="120" align="center">
          <template slot-scope="scope">
            {{ reservationOrigins(scope.row) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn
          v-if="showColumn('replyStatus', tableFilter)"
          prop="AppointmentOrder.replyStatus"
          :label="$t('reservationList.table.replyStatus.title')"
          width="130"
          align="center"
        >
          <template slot-scope="scope">
            <Tag
              v-if="get(scope.row, 'AppointmentOrder.replyStatus')"
              :type="replyStatus(scope.row).tagType"
              disable-transitions
            >
              {{ replyStatus(scope.row).name }}
            </Tag>
            <span v-else>-</span>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('createdAt', tableFilter)" prop="createdAt" :label="$t('reservationList.table.createdAt.title')" width="120" align="center">
          <template slot-scope="scope">
            {{ createdAtFormat(scope.row.createdAt) }}
          </template>
        </BaseElTableColumn>

        <!-- 定金欄位 -->
        <BaseElTableColumn v-if="showColumn('orderPrice', tableFilter) && useDeposit" :label="$t('reservationList.table.orderPrice.title')" prop="status" width="105" align="center">
          <template slot-scope="scope">
            {{ orderTotalPrice(scope.row) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('depositPrice', tableFilter) && useDeposit" :label="$t('reservationList.table.depositPrice.title')" prop="status" width="105" align="center">
          <template slot-scope="scope">
            {{ depositPrice(scope.row) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('paymentType', tableFilter) && useDeposit" :label="$t('reservationList.table.paymentType.title')" prop="status" width="150" align="center">
          <template slot-scope="scope">
            {{ depositPaymentType(scope.row) }}
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn v-if="showColumn('userComment', tableFilter)" label="會員備註" width="150" align="center">
          <template slot-scope="scope">
            {{ getData(scope.row, 'userComment') || '-' }}
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn v-if="showColumn('adminComment', tableFilter)" label="店家備註" width="150" align="center">
          <template slot-scope="scope">
            {{ getData(scope.row, 'AppointmentOrder.adminComment') || '-' }}
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn v-if="showColumn('cancelReasonCode', tableFilter)" label="取消原因" width="150" align="center">
          <template slot-scope="scope">
            <template v-if="getData(scope.row, 'AppointmentOrder.status') === 'cancel' && getData(scope.row, 'AppointmentOrder.cancelReasonCode')">
              <Tag disable-transitions :type="get(cancelReasonConfig[getData(scope.row, 'AppointmentOrder.cancelReasonCode')], 'tag')">
                {{ get(cancelReasonConfig[getData(scope.row, 'AppointmentOrder.cancelReasonCode')], 'label') }}
              </Tag>
              <p class="text-gray-60 text-xs mt-1">
                {{ get(cancelSourceConfig[getData(scope.row, 'AppointmentOrder.cancelSource')], 'label', '-') }}
              </p>
            </template>
            <span v-else>-</span>
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn v-if="showColumn('cancelReasonNote', tableFilter)" label="取消備註" width="150" align="center">
          <template slot-scope="scope">
            {{ getData(scope.row, 'AppointmentOrder.cancelReasonNote') || '-' }}
          </template>
        </BaseElTableColumn>

        <CustomFlowTableColumns
          :customFlowConfig="displayCustomFlowConfig"
          :tableFilterConfig="tableFilter"
          scope="appointment"
        />

        <BaseElTableColumn v-if="showColumn('checkoutTotalPrice', tableFilter)" :label="$t('reservationList.table.checkoutTotalPrice.title')">
          <template slot-scope="scope">
            {{ getData(scope.row, 'AppointmentOrder.AppointmentCheckoutOrder.totalPrice', '-') }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('checkoutPaymentType', tableFilter)" :label="$t('reservationList.table.checkoutPaymentType.title')" width="120px" align="center">
          <template slot-scope="scope">
            {{ displayCheckoutPaymentType(scope.row) }}
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn
          v-if="showColumn('checkoutPaymentStatus', tableFilter)"
          :label="$t('reservationList.table.checkoutPaymentStatus.title')"
          prop="status"
          width="105"
          align="center"
        >
          <template slot-scope="reservation">
            <Tag
              v-if="getData(reservation.row, 'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutPayment') && !isBatchOrder(reservation.row)"
              :type="paymentStatusTagType(reservation.row, 'checkout')"
              disable-transitions
            >
              {{ paymentStatus(reservation.row, 'checkout') }}
            </Tag>
            <span v-else>-</span>
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn
          v-if="showColumn('depositPaymentStatus', tableFilter) && useDeposit"
          :label="$t('reservationList.table.depositPaymentStatus.title')"
          prop="status"
          width="105"
          align="center"
        >
          <template slot-scope="reservation">
            <Tag
              v-if="getData(reservation.row, 'AppointmentOrder.AppointmentOrderDeposit') && !isBatchOrder(reservation.row)"
              :type="paymentStatusTagType(reservation.row, 'deposit')"
              disable-transitions
            >
              {{ paymentStatus(reservation.row, 'deposit') }}
            </Tag>
            <span v-else>-</span>
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn
          v-if="showColumn('batchOrderStatus', tableFilter)"
          :label="$t('reservationList.table.batchOrderStatus.title')"
          prop="status"
          width="105"
          align="center"
        >
          <template slot-scope="reservation">
            <Tag v-if="isBatchOrder(reservation.row)" disable-transitions :type="getData(batchOrderStatus(reservation.row), 'tagType')">
              {{ getData(batchOrderStatus(reservation.row), 'name') }}
            </Tag>
            <span v-else>-</span>
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn
          v-if="showColumn('orderSignatureStatus', tableFilter) && checkAction('planModule.signatureTemplate.enable')"
          :label="$t('reservationList.table.orderSignatureStatus.title')"
          prop="status"
          width="120"
          align="center"
        >
          <template slot-scope="reservation">
            <!-- If the order is a batch/merged order, show '-' -->
            <template v-if="isBatchOrder(reservation.row)">
              -
            </template>
            <template v-else>
              <Tag
                v-if="getOrderSignatureStatus(reservation.row).tagType"
                :type="getOrderSignatureStatus(reservation.row).tagType"
                disable-transitions
              >
                {{ getOrderSignatureStatus(reservation.row).status }}
              </Tag>
              <span v-else>{{ getOrderSignatureStatus(reservation.row).status }}</span>
            </template>
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn fixed="right" :label="$t('reservationList.table.status.title')" prop="status" width="105" align="center">
          <template slot-scope="reservation">
            <Tag disable-transitions :type="orderStatus(getData(reservation.row, 'AppointmentOrder.status'), 'tag')">
              <!-- {{ statusTrans(getData(reservation.row, 'AppointmentOrder.status', '')) }} -->
              {{ $t(orderStatus(getData(reservation.row, 'AppointmentOrder.status'), 'name')) }}
            </Tag>
          </template>
        </BaseElTableColumn>

        <BaseElTableColumn fixed="right" :label="$t('common.table.action.text')" width="60" align="center">
          <template slot-scope="reservation">
            <TableSettingIconButton :testName="`detail-reservation-${reservation.$index}`" @click="onDetail(reservation.row)" />
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="reservationsCount"
        @pageChange="refresh"
      />
    </section>

    <!-- 預約資訊側邊彈窗 -->
    <ReservationOrderDetail
      v-if="showInfo"
      :orderId="selectRow.id"
      :selecReservation="selectRow"
      :customFlowConfig="displayCustomFlowConfig"
      :enableCancelReasonCollection="enableCancelReasonCollection"
      @close="showInfo=false"
      @cancel="onCancel"
      @refresh="refresh"
      @goOrder="onGoOrder"
    />

    <!-- 關閉時段 Dialog -->
    <TogglePeriodDialog v-if="togglePeriodDialog" :title="$t('shiftCalendar.dialog.editClosePeriod.title')" width="440px" @close="togglePeriodDialog = false" />

    <DeleteDialog
      v-if="deleteDialog"
      title="警示"
      :content="deleteContent"
      width="40%"
      btnString="取消預約"
      cancelBtnString="返回"
      @close="deleteDialog = false"
      @delete="cancelReservation()"
    />

    <ExportOptionsDialog
      v-if="showExportOptions"
      :useExportTask="useExportTask"
      allRange
      @close="showExportOptions = false"
      @export="prepareExport"
    />

    <ExportDialog
      v-if="exportState.modal"
      title="匯出預約紀錄"
      :inProgress="exportState.exportting"
      :isError="exportState.error"
      :content="exportState.content"
      :percentage="totalPercentege"
      @close="resetExport"
    />
  </div>
</template>

<script>
import ExportOptionsDialog from '@/components/Dialog/ExportOptionsDialog.vue'
import ExportDialog from '@/components/Dialog/ExportDialog.vue'
import CustomFlowSelect from '@/components/Select/CustomFlowSelect.vue'
import PageTitleExtra from '@/components/Title/PageTitleExtra.vue'
import TableFilter from '@/components/Button/TableFilter.vue'
import { reservationListFilterOptions as reservationListFilterOptionsConfig } from '@/config/table'
import { mapGetters } from 'vuex'
import ProgressBar from '@/components/Progress/ProgressBar'
import ReservationOrderDetail from '@/components/Reservation/ReservationOrderDetail/ReservationOrderDetail.vue'
import { getTableFilterConfig, showColumn } from '@/utils/tableFilter'
import {
  GetReservation,
  GetReservationCount,
  UpdateReservationOrderStatus,
  GetReservationOrderCount,
  FindReservation,
} from '@/api/reservation'
import DeleteDialog from '@/components/Dialog/DeleteDialog.vue'
import TogglePeriodDialog from '@/components/Dialog/TogglePeriodDialog.vue'
import ServiceUnitSearch from '@/components/Search/ServiceUnitSearch.vue'
import ResourceSearch from '@/components/Search/ResourceSearch.vue'
import CustomFlowTableColumns from '@/components/CustomFlowTableColumns.vue'
import BaseSearch from '@/components/Search/BaseSearch.vue'
// import ServiceSearch from '@/components/Search/ServiceSearch.vue'
import { getData } from '@/utils/object'
import EmptyBlock from '@/components/EmptyBlock.vue'
import dayjs from '@/lib/dayjs'
// Utils
import { pageStartIndex } from '@/utils/table'
import {
  dateTimeFormat,
  statusTranslate,
  getAllDataFromApi,
} from '@/utils/helper'
import { getMonth, formatDate } from '@/utils/date'
import { ExportExcel } from '@/utils/excel'

import { reservationOrderOrigins } from '@/utils/reservation'
import { get, map, join, filter, compact, uniqBy, isEmpty, find } from 'lodash'
import { appointmentRiskGuardConfig } from '@/config/appointmentRiskGuard'
import { depositPaymentTypes, depositStatusConfig } from '@/config/deposit'
import { getInvoiceTypeLabel } from '@/config/invoice'
import { batchOrderStatusConfig, orderStatusConfig, replyStatusConfig, cancelReasonConfig, cancelSourceConfig } from '@/config/reservation'
import { paymentOptionsConfig } from '@/config/payment'
import { invoiceStatusConfig } from '@/config/pubApt'
import { usePermissions } from '@/use/permissions'
import { useExport } from '@/use/export'
import { useShop } from '@/use/shop'
import { reactive, ref, computed, onMounted } from 'vue'
import { useCustomFlow } from '@/use/useCustomFlow'
import { usePayment } from '@/use/usePayment'
import { useExternalWallet } from '@/use/useExternalWallet'
import { useReservation } from '@/use/useReservation'
import { useReservationRecommendedServices } from '@/use/useAppointmentServicePairing'
import { i18n } from '@/plugins/i18n/i18n'
import { CreateAppointmentReservationSheetExportTask } from '@/api/exportTask'
import { GetResourceCategory } from '@/api/resourceCategory'
import { useExportCenter } from '@/use/useExportCenter'
import { useCurrency } from '@/use/useCurrency'
import { useRoute } from 'vue-router/composables'
import { apiFormatAdaptor } from '@/utils/api'

export default {
  name: 'ReservationsList',
  components: {
    TableFilter,
    ProgressBar,
    DeleteDialog,
    TogglePeriodDialog,
    ServiceUnitSearch,
    ResourceSearch,
    // ServiceSearch,
    ReservationOrderDetail,
    EmptyBlock,
    PageTitleExtra,
    ExportDialog,
    ExportOptionsDialog,
    CustomFlowTableColumns,
    CustomFlowSelect,
    BaseSearch,
  },
  setup (props, { emit }) {
    const route = useRoute()
    const { checkAction } = usePermissions()
    const { exportState, resetExport, getExportDataOld, totalDataCount, totalPercentege } = useExport()
    const { getDisplayPaymentType } = usePayment()
    const { externalWalletName, getExternalWalletConfig } = useExternalWallet()
    const { configData, getConfig } = useReservation()
    const {
      customFlowConfig,
      displayCustomFlowConfig, getCustomFlowConfig, useCustomFlowFeature,
      getExportCustomFlowFieldsData,
    } = useCustomFlow('appointment')
    const { shopId, shopTimezone } = useShop()
    const { displayCurrencySymbol } = useCurrency()
    const showExportOptions = ref(false)
    const enableCancelReasonCollection = computed(() => get(configData.reservation, 'enableCancelReasonCollection', false))
    const selectRow = ref({
      AppointmentService: {},
      AppointmentUnit: {},
      AppointmentServiceAttaches: [],
    })
    const showInfo = ref(false)
    const tableFilter = ref([])
    const reservationsList = ref([])

    const search = reactive({
      search: '',
      service: '',
      resource: '',
      resourceCategory: '',
      unit: '',
      date: '',
      code: '',
      customFlow: '',
      customFlowId: null,
      status: '',
      replyStatus: '',
      cancelReasonCode: '',
    })
    const useExportTask = computed(() => checkAction('admin.appointmentReservation.createSheetExportTask'))
    const { messageOptions } = useExportCenter()
    const showCustomFlow = computed(() => get(useCustomFlowFeature, 'value.config') && !isEmpty(displayCustomFlowConfig.value)) // 有開權限以及有自定義流程
    const customFlowList = computed(() =>
      get(find(displayCustomFlowConfig.value, { id: search.customFlow }), 'nodes'),
    )

    const batchOrderStatus = computed(() => {
      return (row) => {
        const status = get(row, 'AppointmentOrder.AppointmentBatchOrder.StandardPaymentOrder.status')
        return {
          tagType: get(batchOrderStatusConfig, `${status}.tag`),
          name: i18n.t(get(batchOrderStatusConfig, `${status}.name`)),
        }
      }
    })

    const replyStatus = computed(() => {
      return (row) => {
        const status = get(row, 'AppointmentOrder.replyStatus')
        return {
          tagType: get(replyStatusConfig, `${status}.tag`),
          name: i18n.t(get(replyStatusConfig, `${status}.name`)),
        }
      }
    })

    // 訂單回覆狀態顯示文字（用於下拉選單）
    const replyStatusContent = (status) => {
      if (!status) return '-'
      return i18n.t(get(replyStatusConfig, `${status}.name`))
    }

    const statusContent = (status) => statusTranslate(status)

    const showControl = computed(() => {
      const controls = {
        riskLevelColumn: checkAction('planModule.appointmentRiskGuard.enable'),
        quickPaymentSection: checkAction('planModule.appointmentRiskGuard.enable'),
      }
      return controls
    })

    const displayText = computed(() => ({
      riskLevel: {
        title: '風險等級',
        headerTooltip: '可透過快速收款連結，向中/高風險消費者請求預先付款。',
      },
      quickPayment: {
        linkText: '快速收款連結：',
        copyTooltip: '複製連結',
        notSetText: '尚未設定快速收款連結',
        notSetTooltip: '尚未設定快速收款連結，請聯繫集團管理員於基本參數設定中設定快速收款連結。',
        copySuccessMessage: '付款連結已複製，後續收款狀況可由訂單資訊中的「店家備註」進行記錄與追蹤。',
      },
    }))

    const displayData = computed(() => {
      const adminQuickPaymentUrl = get(configData.reservation, 'adminQuickPaymentUrl', '')
      return {
        hasQuickPaymentUrl: !!adminQuickPaymentUrl,
        quickPaymentUrl: adminQuickPaymentUrl,
      }
    })

    // 取得風險等級顯示配置
    const getRiskLevelDisplay = (riskLevel) => {
      if (!riskLevel) {
        return appointmentRiskGuardConfig.riskLevels.null
      }
      return appointmentRiskGuardConfig.riskLevels[riskLevel] || appointmentRiskGuardConfig.riskLevels.null
    }

    // 複製快速付款連結
    const onCopyQuickPaymentUrl = () => {
      const quickPaymentUrl = displayData.value.quickPaymentUrl
      if (quickPaymentUrl) {
        navigator.clipboard.writeText(quickPaymentUrl)
        window.$message.success(displayText.value.quickPayment.copySuccessMessage)
      }
    }

    // 計算訂單簽署狀態
    const getOrderSignatureStatus = (row) => {
      const signatureMappingStatus = get(row, 'signatureMappingStatus')

      if (!signatureMappingStatus) {
        return { status: '-', tagType: null }
      }

      switch (signatureMappingStatus) {
      case 'allCompleted':
        return { status: '全部完成', tagType: 'action' }
      case 'partiallyCompleted':
        return { status: '部分完成', tagType: 'warning' }
      case 'notCompleted':
        return { status: '未完成', tagType: 'danger' }
      case 'notAppointmentUser':
        return { status: '非預約者', tagType: 'info' }
      default:
        return { status: '-', tagType: null }
      }
    }

    const paymentStatus = (row, type) => {
      if (type === 'deposit') {
        const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
        if (!depositData) return '-'
        const status = depositData.status
        const paidType = depositData.paidType
        // 當付款方式為免付定金且狀態為已付款時,顯示「免付定金」
        const displayStatus = (paidType === 'free' && status === 'paid') ? 'free' : status
        return i18n.t(get(depositStatusConfig[displayStatus], 'name'))
      } else if (type === 'checkout') {
        const depositData = get(row, 'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutPayment')
        if (!depositData) return '-'
        const status = depositData.status
        return i18n.t(get(depositStatusConfig[status], 'name'))
      }
    }

    const displayTableData = computed(() => {
      const data = []
      return data
    })

    // !! 暫時作法，以後需棄用或重構
    // ?? 是否顯示付款狀態 (定金、結帳)
    const isBatchOrder = computed(() => {
      return (row) => {
        const orderData = get(row, 'AppointmentOrder')
        if (get(orderData, 'hasBatchOrder')) {
          return true
        }

        return false
      }
    })

    const showNotSpecify = (row) => {
      const notSpecify = row.fromNotSpecify
      if (row.AppointmentUnit) return notSpecify ? `${row.AppointmentUnit?.name} (不指定)` : row.AppointmentUnit?.name
      else return '-'
    }
    const reservationOrigins = (row) => {
      const notSpecify = row.fromNotSpecify
      const origin = getData(row, 'AppointmentOrder.origin', '')
      const originText = getData(row, 'AppointmentOrder.originText', '')
      return reservationOrderOrigins({ notSpecify, origin, originText })
    }
    const depositPaymentType = (row) => {
      const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
      if (!depositData) return '-'
      const type = depositData.paidType
      if (type === 'offline') {
        const comment = depositData.paidTypeComment
        return i18n.t(get(depositPaymentTypes[comment], 'name'))
      }
      if (type === 'externalWallet') {
        return externalWalletName.value
      }
      return i18n.t(get(depositPaymentTypes[type], 'name'))
    }
    const displayCheckoutPaymentType = (row) => {
      const checkoutOrder = get(row, 'AppointmentOrder.AppointmentCheckoutOrder')
      const checkoutPaymentType = get(checkoutOrder, 'AppointmentCheckoutPayment.paidType')
      const checkoutPaymentTypeComment = get(checkoutOrder, 'AppointmentCheckoutPayment.paidTypeComment')
      const displayCheckoutPaidType = getDisplayPaymentType({
        paidType: checkoutPaymentType,
        paidTypeComment: checkoutPaymentTypeComment,
      })
      if (checkoutPaymentType === 'externalWallet') return externalWalletName.value

      return displayCheckoutPaidType
    }

    const formatExportData = (exportData) => {
      const data = []
      let haveDeposit = false

      exportData.forEach((item) => {
        const order = get(item, 'AppointmentOrder', {})
        const deposit = get(item, 'AppointmentOrder.AppointmentOrderDeposit')
        const attachServices = get(item, 'AppointmentServiceAttaches', [])

        if (deposit) haveDeposit = true
        const classTicketSerials = get(item, 'AppointmentReservationTicket.BookedRecordUse.ClassTicketRecordSerials') || []

        const customFlowFields = getExportCustomFlowFieldsData({
          displayCustomFlowConfig: displayCustomFlowConfig.value,
          itemData: item,
        })

        const checkoutOrder = get(item, 'AppointmentOrder.AppointmentCheckoutOrder')
        const checkoutPaymentType = get(checkoutOrder, 'AppointmentCheckoutPayment.paidType')
        const checkoutPaymentTypeComment = get(checkoutOrder, 'AppointmentCheckoutPayment.paidTypeComment')
        const displayCheckoutPaidType = getDisplayPaymentType({
          paidType: checkoutPaymentType,
          paidTypeComment: checkoutPaymentTypeComment,
        })

        const checkoutInvoiceType = get(checkoutOrder, 'AppointmentCheckoutInvoice.InvoiceContent.buyer.type')
        const checkoutCarrierType = get(checkoutOrder, 'AppointmentCheckoutInvoice.InvoiceContent.buyer.carrierType')
        const checkoutInvoiceStatus = get(checkoutOrder, 'AppointmentCheckoutInvoice.status')
        const row = {
          訂單編號: get(order, 'code', '-'),
          預約單號: item.numberCode,
          訂單狀態: statusContent(item.status) || '-',
          付款狀態: paymentStatus(item, 'deposit'),
          訂單類型: get(order, 'peopleCount', 1) > 1 ? '多人' : '單人',
          會員編號: item.MemberId || '-',
          姓名: item.userName || '未填寫',
          電話號碼: item.userPhone || '未填寫',
          預約日期: dateTimeFormat(item.start).date,
          預約時間: dateTimeFormat(item.start).time,
          服務人員: showNotSpecify(item) || '-',
          服務設備: get(item, 'ResourceItem.name', '-'),
          預約收件地點: get(item, 'AppointmentOrder.userAddress') || '-',
          車牌: get(item, 'AppointmentOrder.customInfo.keeperVehicle.plate') || '-',
          尺寸: get(item, 'AppointmentOrder.customInfo.keeperVehicle.size') || '-',
          服務項目: get(item, 'AppointmentService.name', '-'),
          附加服務: map(attachServices, 'name').join('、') || '-',
          會員備註: item.userComment || '-',
          店家備註: get(order, 'adminComment') || '-',
          取消原因: (order.status === 'cancel' && get(cancelReasonConfig[order.cancelReasonCode], 'label')) || '-',
          取消備註: (order.status === 'cancel' && order.cancelReasonNote) || '-',
          取消來源: (order.status === 'cancel' && get(cancelSourceConfig[order.cancelSource], 'label')) || '-',
          預約來源: reservationOrigins(item),
          訂單回覆狀態: get(item, 'AppointmentOrder.replyStatus')
            ? this.replyStatusContent(get(item, 'AppointmentOrder.replyStatus'))
            : '-',
          建立時間: formatDate(item.createdAt),
          訂單總額: item.totalPrice || '-',
          應付定金: get(deposit, 'depositPrice', '-'),
          付款方式: depositPaymentType(item),
          交易序號: get(deposit, 'code', '-'),
          堂票名稱: get(item, 'AppointmentReservationTicket.ClassTicketRecord.name') || '-',
          堂票總價: get(item, 'AppointmentReservationTicket.ClassTicketRecord.price') || '-',
          堂票序號: classTicketSerials.length ? join(map(classTicketSerials, 'code'), '、') : '-',
          ...customFlowFields,
          結帳付款狀態: paymentStatus(item, 'checkout'),
          結帳總額: get(checkoutOrder, 'totalItemsPrice', '-'),
          實付金額: get(checkoutOrder, 'totalPrice', '-'),
          結帳付款方式: displayCheckoutPaidType,
          結帳交易序號: get(checkoutOrder, 'AppointmentCheckoutPayment.code', '-'),
          發票狀態: i18n.t(get(invoiceStatusConfig, `${checkoutInvoiceStatus}.label`)) || '-',
          發票種類: getInvoiceTypeLabel({ buyerType: checkoutInvoiceType, carrierType: checkoutCarrierType }),
          公司抬頭: get(item, '.AppointmentCheckoutInvoice.InvoiceContent.buyer.name', '-'),
          統一編號: get(item, 'AppointmentCheckoutInvoice.InvoiceContent.buyer.identifier', '-'),
          發票號碼: get(item, 'AppointmentCheckoutInvoice.InvoiceContent.invoiceNo', '-'),
          訂單簽署狀態: this.checkAction('planModule.signatureTemplate.enable') ? this.getOrderSignatureStatus(item).status : '-',
        }

        data.push(row)
      })
      if (!haveDeposit) {
        data.forEach(row => {
          delete row['付款狀態']
          delete row['應付定金']
          delete row['付款方式']
          delete row['交易序號']
        })
      }
      if (!this.checkAction('planModule.signatureTemplate.enable')) {
        data.forEach(row => {
          delete row['訂單簽署狀態']
        })
      }
      return data
    }

    const prepareExport = async () => {
      showExportOptions.value = false
      let dateStart
      let dateEnd
      if (search.date !== '' && search.date) {
        // const {
        //   start,
        //   end,
        // } = getDay(dayjs(search.date).format('YYYY/MM/DD'))
        dateStart = search.date[0]
        dateEnd = search.date[1]
      }
      const payload = {
        shopId: shopId.value,
        sort: (dateStart) ? 'start' : undefined,
        sortBy: (dateStart) ? 'ASC' : 'DESC',
        dateStart,
        dateEnd,
        search: !search.search ? undefined : search.search.trim(),
        AppointmentUnitId: !search.unit ? undefined : search.unit.id,
        AppointmentServiceId: !search.service ? undefined : search.service.id,
        ResourceItemId: (search.resource === '') ? undefined : search.resource.id,
        resourceItemCategoryIds: isEmpty(search.resourceCategory) ? undefined : join(map(search.resourceCategory, 'id'), ','),
        code: !search.code ? undefined : search.code.trim(),
        customFlowNodeId: search.customFlowId || undefined,
        includeAppointmentOrderCheckIn: checkAction('admin.checkInPageSetting.page') || checkAction('planModule.selfCheckIn.enable'),
        includeAppointmentReservationTicket: checkAction('admin.classTicket.page'),
        includeCustomFlowRecord: useCustomFlowFeature.value.record,
        includeAppointmentCheckoutOrder: showColumn('checkoutPaymentStatus', tableFilter.value),
        includeAppointmentOrderDeposit: showColumn('depositPaymentStatus', tableFilter.value) || showColumn('depositPrice', tableFilter.value),
        includeSignatureMapping: checkAction('planModule.signatureTemplate.enable'),
        status: (search.status === '') ? undefined : search.status,
        replyStatus: (search.replyStatus === '') ? undefined : search.replyStatus,
        cancelReasonCode: (search.cancelReasonCode === '') ? undefined : search.cancelReasonCode,
      }
      if (useExportTask.value) {
        const {
          shopId,
          dateStart,
          dateEnd,
          code,
          search,
          AppointmentUnitId,
          AppointmentServiceId,
          ResourceItemId,
          resourceItemCategoryIds,
          customFlowNodeId,
          status,
          replyStatus,
          cancelReasonCode,
        } = payload
        const [, err] = await CreateAppointmentReservationSheetExportTask({
          shopId,
          dateStart,
          dateEnd,
          code,
          search,
          AppointmentUnitId,
          AppointmentServiceId,
          ResourceItemId,
          resourceItemCategoryIds,
          customFlowNodeId,
          status,
          replyStatus,
          cancelReasonCode,
          sort: (dateStart) ? 'start' : undefined,
          sortBy: (dateStart) ? 'ASC' : 'DESC',
        })
        if (err) {
          window.$message.error(err)
          return
        }
        window.$message(messageOptions.value)
      } else {
        exportState.modal = true
        exportState.exportting = true

        try {
          const resCount = await GetReservationCount(payload)
          exportState.dataCount = [resCount]
          if (!totalDataCount.value) {
            exportState.content = '尚無資料可匯出'
            exportState.error = true
            window.$message.warning('尚無資料可匯出')
            return
          }

          const resData = await getExportDataOld({ stage: 0, fetchAPI: GetReservation, payload })
          const formatData = formatExportData(resData)
          exportState.success = true
          exportState.content = '匯出完成'
          ExportExcel(formatData, '預約紀錄', '預約紀錄')
        } catch (error) {
          window.$message.error(error.message || error)
          exportState.exportting = false
          exportState.error = true
        }
      }
    }

    const onDetail = (row) => {
      selectRow.value = row
      showInfo.value = true
    }

    // 推薦商品欄位：推薦服務搭配設定
    const { fetchRecommendedServices, recommendedServicesText } = useReservationRecommendedServices()

    onMounted(async () => {
      if (checkAction('admin.appointmentServicePairing.page')) fetchRecommendedServices()
      const queryAppointmentReservationId = get(route.query, 'appointmentReservationId')
      if (queryAppointmentReservationId) {
        const [record, errMsg] = await apiFormatAdaptor(FindReservation)({
          shopId: shopId.value,
          id: queryAppointmentReservationId,
          includeCustomFlowRecord: get(useCustomFlowFeature.value, 'record'),
        })
        if (errMsg) {
          window.$message.error(errMsg)
        }
        if (record) {
          onDetail(record)
        }
      }

      await getExternalWalletConfig()
      // 獲取預約配置（包含快速收款連結）
      await getConfig('reservation')
    })
    return {
      recommendedServicesText,
      reservationsList,
      displayTableData,
      showInfo,
      selectRow,
      exportState,
      prepareExport,
      tableFilter,
      showNotSpecify,
      paymentStatus,
      search,
      reservationOrigins,
      depositPaymentType,
      resetExport,
      totalPercentege,
      showExportOptions,
      checkAction,
      customFlowConfig,
      getCustomFlowConfig,
      displayCustomFlowConfig,
      useCustomFlowFeature,
      showCustomFlow,
      customFlowList,
      onDetail,
      paymentOptionsConfig,
      displayCheckoutPaymentType,
      useExportTask,
      displayCurrencySymbol,
      GetResourceCategory,
      isBatchOrder,
      batchOrderStatus,
      replyStatus,
      replyStatusContent,
      shopTimezone,
      statusContent,
      getOrderSignatureStatus,
      // 預約風控守門員功能
      showControl,
      getRiskLevelDisplay,
      onCopyQuickPaymentUrl,
      displayText,
      displayData,
      enableCancelReasonCollection,
      cancelReasonConfig,
      cancelSourceConfig,
    }
  },
  data: () => ({
    // Export

    exportting: false,
    exportError: false,
    exportData: null,
    exportPercentage: 0,
    showExportDialog: false,

    tableRefresh: false,
    tableName: 'reservationList',
    // tableFilter: [],
    deleteContent: '',
    loading: false,
    deleteDialog: false,
    dialogType: '',
    togglePeriodDialog: false,
    options: {
      date: new Date(),
      name: '',
      service: '',
      serviceUnit: '',
    },
    reservationsCount: 0,
    monthRecordCount: 0,
    tableOptions: {
      page: 1,
      pageLimit: 10,
    },
    colorList: [{
      color: '#1989fa',
      percentage: 80,
    },
    {
      color: '#f56c6c',
      percentage: 100,
    },
    ],
  }),
  computed: {
    ...mapGetters([
      'shop',
      'userPlanLimit',
      'userFeatures',
      'userPlanFeature',
    ]),
    reservationListFilterOptions () {
      const blockList = []
      if (!this.useDeposit) blockList.push(...['orderPrice', 'depositPrice', 'paymentType'])
      if (!this.useMemberAddress) blockList.push('userAddress')
      if (!this.checkAction('admin.resourceItem.page')) blockList.push('resourceItem')
      if (!this.checkAction('planModule.keeper.enabled')) blockList.push(...['vehiclePlate', 'vehicleSize'])
      if (!this.checkAction('admin.checkInPageSetting.page')) {
        if (this.checkAction('planModule.selfCheckIn.enable')) blockList.push('checkInBranch')
        else blockList.push(...['checkInTime', 'checkInBranch'])
      }
      if (!this.checkAction('planModule.signatureTemplate.enable')) blockList.push('orderSignatureStatus')
      if (!this.checkAction('admin.appointmentServicePairing.page')) blockList.push('recommendedServices')

      const customFlows = compact(map(this.customFlowConfig, i => {
        if (i.enable) return { label: i.name, value: i.name }
        else return null
      }))

      reservationListFilterOptionsConfig.push(...customFlows)

      return uniqBy(filter(reservationListFilterOptionsConfig, i => !blockList.includes(i.value)), option => option.value)
    },
    pageStartIndex () {
      return pageStartIndex(this.tableOptions.page, this.tableOptions.pageLimit)
    },
    contractReservationsCount () {
      return this.userPlanLimit.appointmentReservationLimitPerMonth
    },
    reservationsPercentage () {
      if (this.userPlanLimit.appointmentReservationLimitPerMonth === 0) {
        return 0
      }
      const count = Math.round(this.monthRecordCount * 100 / this.contractReservationsCount)
      return count > 100 ? 100 : count
    },
    useDeposit () {
      return this.checkAction('adminView.appointmentDeposit.enable')
    },
    useMemberAddress () {
      return this.checkAction('adminView.member.findUserInfoMoreAddress')
    },
  },
  watch: {
    reservationListFilterOptions () {
      this.tableFilter = getTableFilterConfig(this.tableName, this.reservationListFilterOptions)
    },
  },

  async mounted () {
    this.tableFilter = getTableFilterConfig(this.tableName, this.reservationListFilterOptions)
    const queryOrderCode = get(this.$route.query, 'code')

    // 查詢預約訂單，自動開啟至詳情 drawer
    if (queryOrderCode) {
      this.search.code = queryOrderCode
      await this.refresh()
      const row = this.reservationsList.find(reservation => reservation.code === queryOrderCode)
      if (row) this.onDetail(row)
      return
    }
    await this.refresh()
  },
  methods: {
    showColumn,
    getData,
    get,
    onCancel (data, cancelReason) {
      // 取消原因由抽屜內部的彈窗收集後一併帶回，直接送出
      if (cancelReason) {
        this.cancelReservation(cancelReason)
        return
      }
      const orders = get(data, 'AppointmentOrder.AppointmentReservations')
      const orderCode = get(data, 'AppointmentOrder.code')
      if (!orders) {
        this.deleteDialog = true
        this.deleteContent = i18n.t('reservationList.message.cancelConfirm.title')
        return
      }
      if (!orders.length || orders.length === 1) {
        this.deleteDialog = true
        this.deleteContent = i18n.t('reservationList.message.cancelConfirm.title')
        return
      }

      const ordersCount = orders.length

      this.deleteContent = i18n.t('reservationList.message.cancelConfirm.content', { orderCode: orderCode, ordersCount: ordersCount })
      this.deleteDialog = true
    },
    dateTime (dateTime) {
      return dateTimeFormat(dateTime, this.shopTimezone)
    },
    statusTrans (status) {
      return statusTranslate(status)
    },

    orderStatus (status, attr) {
      return get(orderStatusConfig[status], attr)
    },
    tagType (val) {
      let type = 'info'
      if (val === 'complete') type = 'action'
      if (val === 'wait') type = 'info'
      if (val === 'cancel') type = 'danger'
      return type
    },
    async refresh (force = false) {
      this.loading = true
      if (force === true) this.tableOptions.page = 1
      await Promise.all([
        this.getReservation(),
        this.getReservationCount(),
        this.getMonthReservation(),
        this.getCustomFlowConfig(),
      ])
      this.loading = false
    },

    onGoOrder (orderCode) {
      this.showInfo = false
      this.selectRow = null
      this.search.code = orderCode

      this.refresh(true)
    },

    //= > 取得預約
    async getReservation () {
      try {
        let dateStart
        let dateEnd
        if (this.search.date !== '' && this.search.date) {
          // const {
          //   start,
          //   end,
          // } = getDay(dayjs(this.search.date).format('YYYY/MM/DD'))
          dateStart = this.search.date[0]
          dateEnd = this.search.date[1]
        }
        this.search.code = this.search.code.trim()
        const res = await GetReservation({
          shopId: this.shop,
          start: this.pageStartIndex,
          limit: this.tableOptions.pageLimit,
          sort: (dateStart) ? 'start' : undefined,
          sortBy: (dateStart) ? 'ASC' : 'DESC',
          dateStart,
          dateEnd,
          search: (this.search.search === '') ? undefined : this.search.search.trim(),
          AppointmentUnitId: (this.search.unit === '') ? undefined : this.search.unit.id,
          AppointmentServiceId: (this.search.service === '') ? undefined : this.search.service.id,
          ResourceItemId: (this.search.resource === '') ? undefined : this.search.resource.id,
          resourceItemCategoryIds: isEmpty(this.search.resourceCategory) ? undefined : join(map(this.search.resourceCategory, 'id'), ','),
          code: (this.search.code === '') ? undefined : this.search.code.trim(),
          includeAppointmentOrderCheckIn: this.checkAction('admin.checkInPageSetting.page') || this.checkAction('planModule.selfCheckIn.enable'),
          includeAppointmentReservationTicket: this.checkAction('admin.classTicket.page'),
          includeCustomFlowRecord: this.useCustomFlowFeature.record,
          includeAppointmentOrderDeposit: showColumn('depositPaymentStatus', this.tableFilter) || showColumn('depositPrice', this.tableFilter),
          includeAppointmentCheckoutOrder: showColumn('checkoutPaymentStatus', this.tableFilter),
          includeSignatureMapping: this.checkAction('planModule.signatureTemplate.enable'),
          customFlowNodeId: this.search.customFlowId || undefined,
          status: (this.search.status === '') ? undefined : this.search.status,
          replyStatus: (this.search.replyStatus === '') ? undefined : this.search.replyStatus,
          cancelReasonCode: (this.search.cancelReasonCode === '') ? undefined : this.search.cancelReasonCode,
        })
        this.reservationsList = res

        this.exportData = res
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },
    async getAllReservation () {
      let dateStart
      let dateEnd
      if (this.search.date !== '' && this.search.date) {
        // const {
        //   start,
        //   end,
        // } = getDay(dayjs(this.search.date).format('YYYY/MM/DD'))
        dateStart = this.search.date[0]
        dateEnd = this.search.date[1]
      }
      const payload = {
        shopId: this.shop,
        start: 0,
        limit: 100,
        sort: (dateStart) ? 'start' : undefined,
        sortBy: (dateStart) ? 'ASC' : 'DESC',
        dateStart,
        dateEnd,
        search: (this.search.search === '') ? undefined : this.search.search.trim(),
        AppointmentUnitId: (this.search.unit === '') ? undefined : this.search.unit.id,
        AppointmentServiceId: (this.search.service === '') ? undefined : this.search.service.id,
        ResourceItemId: (this.search.resource === '') ? undefined : this.search.resource.id,
        code: (this.search.code === '') ? undefined : this.search.code.trim(),
        status: (this.search.status === '') ? undefined : this.search.status,
      }
      this.exportData = await getAllDataFromApi(
        this.reservationsCount,
        GetReservation,
        payload,
      )
    },
    //= > 取得本月預約數
    async getMonthReservation () {
      try {
        const now = dayjs(new Date())
        const year = now.format('YYYY')
        const month = now.format('MM')
        const {
          start,
          end,
        } = getMonth(year, month)

        const [res, err] = await GetReservationOrderCount({
          shopId: this.shop,
          dateStart: start,
          dateEnd: end,
          status: (this.search.status === '') ? undefined : this.search.status,
        })
        if (err) return this.$message.error(err)
        this.monthRecordCount = res.count || 0
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },
    //= > 取得預約總數
    async getReservationCount () {
      try {
        let dateStart
        let dateEnd
        if (this.search.date !== '' && this.search.date) {
          // const {
          //   start,
          //   end,
          // } = getDay(dayjs(this.search.date).format('YYYY/MM/DD'))
          dateStart = this.search.date[0]
          dateEnd = this.search.date[1]
        }
        this.reservationsCount = await GetReservationCount({
          shopId: this.shop,
          dateStart,
          dateEnd,
          search: !this.search.search ? undefined : this.search.search.trim(),
          AppointmentUnitId: !this.search.unit ? undefined : this.search.unit.id,
          AppointmentServiceId: !this.search.service ? undefined : this.search.service.id,
          ResourceItemId: (this.search.resource === '') ? undefined : this.search.resource.id,
          resourceItemCategoryIds: isEmpty(this.search.resourceCategory) ? undefined : join(map(this.search.resourceCategory, 'id'), ','),
          code: !this.search.code ? undefined : this.search.code.trim(),
          customFlowNodeId: this.search.customFlowId || undefined,
          includeSignatureMapping: this.checkAction('planModule.signatureTemplate.enable'),
          status: (this.search.status === '') ? undefined : this.search.status,
          replyStatus: (this.search.replyStatus === '') ? undefined : this.search.replyStatus,
        })
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },
    //= > 刪除(取消)預約
    async cancelReservation ({ cancelReasonCode, cancelReasonNote } = {}) {
      const [, err] = await UpdateReservationOrderStatus({
        shopId: this.shop,
        id: get(this.selectRow, 'AppointmentOrder.id'),
        status: 'cancel',
        cancelReasonCode,
        cancelReasonNote,
      })
      if (err) return this.$message.error(err)
      window.$message.success(i18n.t('common.message.cancelSuccess'))
      this.deleteDialog = false
      // this.showInfo = false
      await this.refresh()
      this.showInfo = false
      // setTimeout(() => {
      //   this.showInfo = true
      // }, 50)
    },

    async updateTableFilter () {
      this.tableRefresh = true
      this.tableFilter = getTableFilterConfig(this.tableName)
      await this.refresh()
      setTimeout(() => {
        this.tableRefresh = false
      }, 0)
    },

    orderTotalPrice (row) {
      const orderPrice = get(row, 'AppointmentOrder.totalPrice')
      if (!orderPrice) return '-'
      return `${this.displayCurrencySymbol} ${orderPrice}`
    },

    depositPrice (row) {
      const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
      if (!depositData) return '-'
      if (depositData.depositPrice === 0) return '-'
      return `${this.displayCurrencySymbol}  ${depositData.depositPrice}`
    },

    paymentStatusTagType (row, type) {
      if (type === 'deposit') {
        const depositData = get(row, 'AppointmentOrder.AppointmentOrderDeposit')
        if (!depositData) return ''
        const status = depositData.status
        const paidType = depositData.paidType
        // 當付款方式為免付定金且狀態為已付款時,使用 free 狀態的 tag
        const displayStatus = (paidType === 'free' && status === 'paid') ? 'free' : status
        return get(depositStatusConfig[displayStatus], 'tag')
      } else if (type === 'checkout') {
        const checkoutData = get(row, 'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutPayment')
        if (!checkoutData) return ''
        const status = checkoutData.status
        return get(depositStatusConfig[status], 'tag')
      }
    },

    createdAtFormat (date) {
      return dayjs(date).format('YYYY/MM/DD HH:mm:ss')
    },
  },
}
</script>

<style scoped lang="postcss">
.reservations-list {
  height: 100%;
}
::v-deep .test.el-select,
::v-deep .test .el-input {
  max-width: 196px !important;
}
::v-deep .test2.el-select,
::v-deep .test2.el-input,
::v-deep .test2 .el-input,
::v-deep.test2.el-input__inner {
  width: 100% !important;
  max-width: 400px !important;
}
.filters-wrapper {
  @apply flex flex-col gap-[20px] mb-[20px];
}
.filter-row {
  @apply flex items-center gap-[8px] flex-col;
}

.filter-row .label {
  @apply flex-shrink-0;
}
.test2.date-picker-width {
  @apply !max-w-[808px];
}
::v-deep .el-date-editor .el-range-input {
  @apply w-[45%];
}
</style>
